// ============================================================================
// BuildLab Indicators — catalog
// ----------------------------------------------------------------------------
// Original BuildLab indicators for futures traders. Each is built from standard,
// public technical-analysis concepts (no third-party code) and ships for both
// TradingView (Pine Script v6) and Tradovate (JavaScript custom indicators).
// Downloadable source lives in /public/downloads/indicators/.
// ============================================================================

export const INDICATOR_CATEGORIES = [
    { key: 'orderflow', label: 'Order Flow & Volume' },
    { key: 'levels', label: 'Levels & Sessions' },
    { key: 'risk', label: 'Risk & Execution' },
    { key: 'trend', label: 'Trend & Volatility' },
    { key: 'positioning', label: 'Positioning & Open Interest' },
];

const Indicators = [
    {
        id: 'institutional',
        slug: 'institutional-indicator',
        name: 'BuildLab Institutional Indicator',
        shortName: 'Institutional Indicator',
        category: 'levels',
        area: 'Overlay (price chart)',
        free: true,
        link: '/institutional-indicator',
        tagline: 'The free flagship — VWAP, pivots, levels, internals & a live bias panel, all in one.',
        summary:
            'BuildLab’s free, transparent read on institutional market structure: daily-anchored VWAP with σ-bands, floor pivots, prior day/week levels, the initial balance and opening range, a market-internals breadth layer, and a plain-English bias panel. A price-and-volume analog to the paid gamma/order-flow tools.',
        bestFor: 'Index & micro futures (ES/MES, NQ/MNQ) intraday.',
        platforms: ['TradingView · Pine Script v6'],
    },
    {
        id: '1',
        slug: 'order-flow-volume-cvd',
        name: 'BuildLab Order-Flow Volume (CVD)',
        shortName: 'Order-Flow Volume (CVD)',
        category: 'orderflow',
        area: 'Separate pane',
        image: '/images/indicators/order-flow-volume-cvd.svg',
        tagline: 'See the net buying vs. selling pressure behind every move.',
        summary:
            'Cumulative Volume Delta signs each bar’s volume by direction and accumulates it — optionally re-anchored each session — so you can see whether a move is actually backed by order flow. On Tradovate it uses true bid/ask volume (offer − bid) for an exchange-accurate read; on TradingView it signs volume by candle direction.',
        bestFor: 'Index & micro futures (ES/MES, NQ/MNQ, GC/MGC), 1–15 min intraday.',
        inputs: [
            { name: 'Delta source / True delta', desc: 'Use bid/ask volume where available (Tradovate), otherwise candle polarity (close vs. open or prior close).' },
            { name: 'Reset each session', desc: 'Re-anchor the cumulative delta to zero at the start of each trading day for clean intraday reads.' },
            { name: 'Signal MA length', desc: 'Length of the moving average drawn over CVD to smooth it and flag pressure shifts.' },
        ],
        howToUse: [
            { t: 'Read the pressure', d: 'Rising CVD = net buyers in control; falling CVD = net sellers. The slope matters more than the absolute number.' },
            { t: 'Hunt divergences', d: 'Price makes a new high but CVD does not (or vice-versa) → the move isn’t backed by order flow. This is the highest-value signal: it flags exhaustion and likely reversals.' },
            { t: 'Use the signal MA', d: 'CVD crossing above/below its moving average marks a shift in short-term pressure — a confirmation filter for entries.' },
            { t: 'Anchor to the session', d: 'Keep “reset each session” on for day trading so each day starts flat; turn it off for a continuous multi-day swing view.' },
        ],
        notes: 'TradingView volume delta is estimated from candle direction; Tradovate’s offer/bid volume is true executed delta. Both are educational tools, not signals to trade blindly.',
        platforms: ['TradingView · Pine Script v6', 'Tradovate · JavaScript'],
        pine: '/downloads/indicators/buildlab-order-flow-volume.pine',
        tradovate: '/downloads/indicators/buildlab-order-flow-volume.js',
        pineVerified: true,
    },
    {
        id: '2',
        slug: 'vwap-bands',
        name: 'BuildLab VWAP Bands',
        shortName: 'VWAP Bands',
        category: 'levels',
        area: 'Overlay (price chart)',
        tagline: 'The institutional average price, with a statistical envelope.',
        summary:
            'Anchored VWAP — the volume-weighted average price, re-anchored each session, week, or month — wrapped in ±1σ and ±2σ standard-deviation bands. It’s the line institutions benchmark fills against, plus a statistical read on how stretched price is from fair value.',
        bestFor: 'Index & micro futures (ES/MES, NQ/MNQ) intraday; mean-reversion and trend-pullback entries.',
        inputs: [
            { name: 'Anchor', desc: 'Re-anchor VWAP each Session/day, Week, or Month.' },
            { name: 'Source', desc: 'Price input for the average — defaults to HLC3 (typical price).' },
            { name: 'Inner / Outer band (σ)', desc: 'Standard-deviation multipliers for the two band pairs (default 1σ and 2σ).' },
        ],
        howToUse: [
            { t: 'Read control', d: 'Price above VWAP = buyers in control on the session; below = sellers. VWAP itself is the magnet institutions trade back toward.' },
            { t: 'Fade the extremes', d: 'Tags of the ±2σ band are statistically stretched — on range/rotation days they tend to mean-revert back toward VWAP.' },
            { t: 'Trend pullbacks', d: 'On trend days, pullbacks into VWAP or the ±1σ band are continuation entries in the trend direction.' },
            { t: 'Anchor to context', d: 'Use Session for day trading, Week/Month for swing context and higher-timeframe fair value.' },
        ],
        notes: 'Bands assume a roughly normal distribution of price around VWAP — they describe stretch, not a guarantee of reversal. Educational tool, not a signal to trade blindly.',
        platforms: ['TradingView · Pine Script v6', 'Tradovate · JavaScript'],
        pine: '/downloads/indicators/buildlab-vwap-bands.pine',
        tradovate: '/downloads/indicators/buildlab-vwap-bands.js',
        pineVerified: true,
    },
    {
        id: '3',
        slug: 'session-map',
        name: 'BuildLab Session Map',
        shortName: 'Session Map',
        category: 'levels',
        area: 'Overlay (price chart)',
        tagline: 'Know which session you’re in — and where its range is forming.',
        summary:
            'Shades the futures sessions (Globex/overnight, RTH, Asia, London) and tracks the developing regular-hours high and low. Instant context for when liquidity and behavior change between sessions.',
        bestFor: 'Index & micro futures intraday; session-based and opening-range strategies.',
        inputs: [
            { name: 'Session windows', desc: 'Editable time windows for Globex, RTH, Asia and London (in exchange time).' },
            { name: 'Exchange timezone', desc: 'Timezone used to align the session shading.' },
            { name: 'Mark RTH High/Low', desc: 'Draw the developing regular-hours high and low as the session builds.' },
        ],
        howToUse: [
            { t: 'Set your context', d: 'Each shaded band shows which participants are active. Overnight/Globex ranges are thinner and tend to mean-revert; RTH is where real volume and trends develop.' },
            { t: 'Trade the RTH range', d: 'The developing RTH high/low are the day’s working boundaries — breakouts and failed breaks off them are high-information events.' },
            { t: 'Watch transitions', d: 'Volatility and direction frequently shift at the London and New York opens — the shading makes those handoffs obvious.' },
        ],
        notes: 'Windows default to CME index-futures hours; adjust for your product and timezone.',
        platforms: ['TradingView · Pine Script v6'],
        pine: '/downloads/indicators/buildlab-session-map.pine',
        tradovate: null,
        pineVerified: true,
    },
    {
        id: '4',
        slug: 'opening-range',
        name: 'BuildLab Opening Range (ORB)',
        shortName: 'Opening Range (ORB)',
        category: 'levels',
        area: 'Overlay (price chart)',
        tagline: 'Lock the opening range and project the breakout targets.',
        summary:
            'Captures the high/low of the opening window (default 09:30–09:45 ET) each day, locks it, and projects it forward with a midpoint and 1R / 2R breakout targets — a transparent framework for opening-range-breakout trading.',
        bestFor: 'Index & micro futures (ES/MES, NQ/MNQ) on 1–5 min intraday.',
        inputs: [
            { name: 'Opening-range window', desc: 'The session window whose high/low define the range.' },
            { name: 'Show 1R / 2R targets', desc: 'Project measured-move targets at one and two times the range.' },
            { name: 'Show midpoint', desc: 'Plot the range midpoint — a common mean-reversion pivot.' },
        ],
        howToUse: [
            { t: 'Wait for the lock', d: 'Let the opening window complete; the range high/low then freeze and extend across the session.' },
            { t: 'Trade the break', d: 'A break-and-hold above the range favors longs toward +1R/+2R; below favors shorts toward −1R/−2R.' },
            { t: 'Respect failure', d: 'A break that snaps back inside the range is a failed breakout — often a fade back toward the midpoint/opposite side.' },
        ],
        notes: 'Default window is the first 15 minutes of the US cash open; change it for your session/strategy.',
        platforms: ['TradingView · Pine Script v6'],
        pine: '/downloads/indicators/buildlab-opening-range.pine',
        tradovate: null,
        pineVerified: true,
    },
    {
        id: '5',
        slug: 'key-levels',
        name: 'BuildLab Key Levels',
        shortName: 'Key Levels',
        category: 'levels',
        area: 'Overlay (price chart)',
        tagline: 'The reference levels institutions and algos actually watch.',
        summary:
            'Plots prior-day high/low/close (settlement), today’s open, prior-week high/low, and the overnight (Globex) high/low — the levels where stops cluster and price reliably reacts.',
        bestFor: 'All futures; intraday and swing context.',
        inputs: [
            { name: 'Daily levels', desc: 'Toggle prior-day high/low, prior-day close (settlement), and today’s open.' },
            { name: 'Weekly levels', desc: 'Toggle prior-week high/low for higher-timeframe context.' },
            { name: 'Overnight window', desc: 'Define the Globex window used for the overnight high/low.' },
        ],
        howToUse: [
            { t: 'Mark the battlegrounds', d: 'Prior-day H/L and the overnight H/L are where resting stops sit — expect reactions, sweeps, and reversals there.' },
            { t: 'Use settlement & open', d: 'Price relative to prior settlement and today’s open is a fast read on whether the day is net bullish or bearish.' },
            { t: 'Confluence', d: 'The highest-odds trades come where a daily and weekly level (or overnight extreme) stack up together.' },
        ],
        notes: 'Levels use prior-period values (no lookahead) so they don’t repaint intraday.',
        platforms: ['TradingView · Pine Script v6'],
        pine: '/downloads/indicators/buildlab-key-levels.pine',
        tradovate: null,
        pineVerified: true,
    },
    {
        id: '6',
        slug: 'risk-calculator',
        name: 'BuildLab Risk Calculator',
        shortName: 'Risk Calculator',
        category: 'risk',
        area: 'On-chart table',
        tagline: 'Position sizing for futures — no more napkin math at the open.',
        summary:
            'Enter your dollar risk per trade plus entry and stop, and it returns risk in points/ticks, dollar risk per contract (using the symbol’s real point value), the number of contracts that fit your risk, and the R:R to your target.',
        bestFor: 'Any futures contract; pre-trade sizing and discipline.',
        inputs: [
            { name: 'Risk per trade ($)', desc: 'The maximum dollars you’ll risk on the trade.' },
            { name: 'Entry / Stop / Target', desc: 'Entry (or current price), your stop, and an optional target for R:R.' },
            { name: 'Table position', desc: 'Where the readout table sits on the chart.' },
        ],
        howToUse: [
            { t: 'Size every trade', d: 'Set your $ risk and stop distance; the “Contracts” figure is the largest position that keeps the trade within that risk.' },
            { t: 'Check the R:R', d: 'Add a target to see reward-to-risk instantly — skip setups that don’t clear your minimum.' },
            { t: 'Trust the point value', d: 'It reads the contract’s real point value, so ES, MES, NQ, MNQ, CL, GC all size correctly with no manual lookup.' },
        ],
        notes: 'Uses the symbol’s point value/tick from TradingView; verify against your broker’s contract specs.',
        platforms: ['TradingView · Pine Script v6'],
        pine: '/downloads/indicators/buildlab-risk-calculator.pine',
        tradovate: null,
        pineVerified: true,
    },
    {
        id: '7',
        slug: 'expected-move',
        name: 'BuildLab Expected Move',
        shortName: 'Expected-Move Bands',
        category: 'trend',
        area: 'Overlay (price chart)',
        tagline: 'Is today’s move normal, or already extended?',
        summary:
            'Projects the day’s statistically expected range as ATR-based bands around the session anchor (open or prior settlement). A fast read on whether price is inside a normal day’s range or stretched.',
        bestFor: 'Index & micro futures; intraday mean-reversion and target-setting.',
        inputs: [
            { name: 'Daily ATR length', desc: 'Lookback for the daily Average True Range that sizes the bands.' },
            { name: 'Anchor', desc: 'Build the bands from today’s open or the prior settlement.' },
            { name: 'Band multipliers', desc: 'Inner/outer band size as multiples of daily ATR (default 0.5× and 1×).' },
        ],
        howToUse: [
            { t: 'Gauge extension', d: 'Inside the inner band = quiet; tagging the outer (±1 ATR) band means the day is already near a full expected range — fade or take profit.' },
            { t: 'Set realistic targets', d: 'Use the bands as objective profit targets instead of guessing how far a move “should” go.' },
            { t: 'Spot trend days', d: 'Closing beyond the outer band signals an above-average, trending day — switch from fading to trend-following.' },
        ],
        notes: 'Uses the prior day’s ATR to project today (no lookahead). A probability envelope, not a hard limit.',
        platforms: ['TradingView · Pine Script v6'],
        pine: '/downloads/indicators/buildlab-expected-move.pine',
        tradovate: null,
        pineVerified: true,
    },
    {
        id: '8',
        slug: 'relative-volume',
        name: 'BuildLab Relative Volume (RVOL)',
        shortName: 'Relative Volume (RVOL)',
        category: 'orderflow',
        area: 'Separate pane',
        tagline: 'Spot the bars institutions are actually active in.',
        summary:
            'Compares each bar’s volume to its recent average. RVOL above 1 is above-average participation; spikes past the high threshold flag the bars where real activity — and the moves most worth trusting — are happening.',
        bestFor: 'All futures; confirming breakouts and filtering low-conviction moves.',
        inputs: [
            { name: 'Average length', desc: 'Lookback for the volume average RVOL is measured against.' },
            { name: 'High-volume threshold', desc: 'RVOL level that marks a genuine participation spike (default 2×).' },
        ],
        howToUse: [
            { t: 'Confirm breakouts', d: 'Breakouts on RVOL ≥ your high threshold have real participation behind them; sub-1 RVOL breakouts are prone to fail.' },
            { t: 'Filter the noise', d: 'Down-weight signals that fire on below-average volume — they’re the chop most likely to trap you.' },
            { t: 'Time the action', d: 'RVOL spikes mark the windows of the day worth being most attentive in.' },
        ],
        notes: 'Compares to a rolling N-bar average on the chart timeframe. Educational tool only.',
        platforms: ['TradingView · Pine Script v6'],
        pine: '/downloads/indicators/buildlab-relative-volume.pine',
        tradovate: null,
        pineVerified: true,
    },
    {
        id: '9',
        slug: 'mtf-bias',
        name: 'BuildLab MTF Bias Dashboard',
        shortName: 'MTF Bias Dashboard',
        category: 'trend',
        area: 'On-chart table',
        tagline: 'Five timeframes of trend, at a glance.',
        summary:
            'A compact table showing trend (price vs. EMA) and RSI across five timeframes at once, so you always know whether you’re trading with or against the higher-timeframe tide.',
        bestFor: 'All futures; trend alignment and confluence filtering.',
        inputs: [
            { name: 'EMA / RSI lengths', desc: 'Lengths used for the trend (EMA) and momentum (RSI) reads.' },
            { name: 'Timeframes 1–5', desc: 'The five timeframes shown — defaults 5m, 15m, 1h, 4h, Daily.' },
            { name: 'Table position', desc: 'Where the dashboard sits on the chart.' },
        ],
        howToUse: [
            { t: 'Trade with the tide', d: 'Take trades in the direction the higher timeframes agree on; when 4h and Daily are bullish, favor longs on the lower frames.' },
            { t: 'Demand alignment', d: 'The cleanest setups are when most/all rows agree. Mixed rows = chop risk — size down or stand aside.' },
            { t: 'Use RSI for timing', d: 'When trend rows align, an RSI pullback on a lower frame is your entry timing within that bias.' },
        ],
        notes: 'Higher-timeframe values update as those bars close. Confluence tool, not a standalone signal.',
        platforms: ['TradingView · Pine Script v6'],
        pine: '/downloads/indicators/buildlab-mtf-bias.pine',
        tradovate: null,
        pineVerified: true,
    },
    {
        id: '10',
        slug: 'cot-monitor',
        name: 'BuildLab COT Monitor',
        shortName: 'COT Monitor',
        category: 'positioning',
        area: 'Separate pane',
        tagline: 'Follow the “smart money” footprint behind the contract.',
        summary:
            'Plots Commitment of Traders positioning for the charted futures product using TradingView’s public CFTC data — the selected group’s longs, shorts, and net position. Auto-detects the COT code from the chart symbol.',
        bestFor: 'Outright futures (ES, NQ, CL, GC, ZB, 6E, etc.); swing and positional context.',
        inputs: [
            { name: 'Trader group', desc: 'Commercial (hedgers), Noncommercial (large speculators), or Nonreportable (small traders).' },
        ],
        howToUse: [
            { t: 'Read the hedgers', d: 'Commercials are the informed hedgers — extreme net-long readings often mark value/bottoms; extreme net-short, tops.' },
            { t: 'Watch the specs', d: 'Noncommercial (large specs) are trend-followers; crowded extremes can precede reversals when positioning unwinds.' },
            { t: 'Confirm, don’t time', d: 'COT updates weekly — use it for positional bias and context, paired with your intraday levels for timing.' },
        ],
        notes: 'Weekly CFTC data (delayed). Requires a futures product with COT coverage; built on Pine v5 to match TradingView’s COT data library.',
        platforms: ['TradingView · Pine Script v5'],
        pine: '/downloads/indicators/buildlab-cot-monitor.pine',
        tradovate: null,
        pineVerified: true,
    },
];

export default Indicators;
